import {
  useCallback,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Input,
  InputDecoration,
  InputGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CountBadge } from '../CountBadge';

export interface FilterChipOption<TValue extends string> {
  value: TValue;
  label: string;
  icon?: ReactNode;
}

type FilterChipVariant = 'default' | 'ghost';

/**
 * Opt-in server-search + pagination mode. When present, the popover stops
 * client-side filtering `options` (they arrive pre-filtered from the server),
 * controls its search input through `search` / `onSearchChange`, and renders a
 * bottom sentinel that calls `onLoadMore` when scrolled into view and
 * `hasNextPage` is true. Absent ⇒ the chip behaves exactly as before (static
 * options, internal client-side search).
 */
export interface FilterChipAsyncOptions {
  /** Controlled search value; the server filters against it. */
  search: string;
  onSearchChange: (next: string) => void;
  /** Shows a loading row while a (initial or next) page is in flight. */
  isLoading?: boolean;
  /** Whether another page is available to fetch. */
  hasNextPage?: boolean;
  /** Called when the bottom sentinel becomes visible and `hasNextPage`. */
  onLoadMore?: () => void;
}

interface FilterChipBaseProps<TValue extends string> {
  label: string;
  options: ReadonlyArray<FilterChipOption<TValue>>;
  /**
   * Label for the reset row at the top of the popover. Defaults to "All".
   * Use "None" for filters where "All" doesn't read naturally (e.g. group-by).
   */
  resetOptionLabel?: string;
  /**
   * Hide the reset / clear ("All") row. Use for REQUIRED single-selects where a
   * value is always present (e.g. a project picker) and clearing is meaningless.
   * Defaults to `false` (reset row shown), preserving existing behavior.
   */
  hideReset?: boolean;
  /**
   * Opt into server-search + pagination. When provided, the search input is
   * controlled by this object and `options` are rendered as-is (no client-side
   * filtering). Omit for the default static-options behavior.
   */
  asyncOptions?: FilterChipAsyncOptions;
  /** Whether to render the search input above the option list. Defaults to `true`. */
  searchable?: boolean;
  searchPlaceholder?: string;
  triggerAriaLabel?: string;
  /**
   * Visual style for the chip frame:
   * - `default` — bordered card with primary-tinted active state (default).
   * - `ghost` — borderless, transparent until hover/active; useful when the
   *   chip sits inside a surface that already provides visual containment.
   */
  variant?: FilterChipVariant;
  /** Extra class names appended to the outer frame. Use for one-off overrides. */
  frameClassName?: string;
  /**
   * Extra class names appended to the inner trigger button. Use for one-off
   * overrides like tighter padding when the chip sits in a dense layout.
   */
  triggerClassName?: string;
  /**
   * What the trigger shows:
   * - `label` (default) — `{label} ▾` when inactive, `{label}: {value}` (single)
   *   or `{label} (count)` (multi) when active.
   * - `value` — always shows the current value (or `resetOptionLabel` when
   *   nothing is selected). The chip label is dropped from the trigger and
   *   should be communicated via `popoverHeader` instead.
   */
  triggerLabelStyle?: 'label' | 'value';
  /**
   * Optional header rendered at the top of the popover content. Useful as
   * a contextual title when `triggerLabelStyle` is `'value'` and the chip
   * label is otherwise not visible.
   */
  popoverHeader?: ReactNode;
  /** Anchor side for the popover content. Defaults to `'bottom'`. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Icon rendered before the label, naming what the chip does (e.g. a group
   * glyph on a Group by chip). Omit for chips whose label already says it.
   */
  leadingIcon?: ReactNode;
  /** Frame height. Defaults to `md`; use `lg` beside a `size="lg"` search input. */
  size?: ChipSize;
}

interface FilterChipSingleProps<TValue extends string> extends FilterChipBaseProps<TValue> {
  mode?: 'single';
  value: TValue | null;
  onChange: (next: TValue | null) => void;
}

interface FilterChipMultiProps<TValue extends string> extends FilterChipBaseProps<TValue> {
  mode: 'multiple';
  value: ReadonlyArray<TValue>;
  onChange: (next: ReadonlyArray<TValue>) => void;
}

export type FilterChipPopoverProps<TValue extends string> =
  | FilterChipSingleProps<TValue>
  | FilterChipMultiProps<TValue>;

/**
 * The chip frame's height. `md` is the toolbar default; `lg` matches the 40px
 * `SearchInput size="lg"`, so a chip beside a search field lines up with it
 * instead of floating 2px inside its box.
 */
type ChipSize = 'md' | 'lg';

const CHIP_FRAME_HEIGHT_CLASS: Record<ChipSize, string> = {
  md: 'h-9',
  lg: 'h-10',
};

/** Caps how wide the chip frame can grow before the value label truncates. */
const CHIP_MAX_WIDTH_CLASS = 'max-w-65';

/**
 * Mirrors the v4 Select / Button focus styling so the chip and inner controls
 * read as field-like surfaces. The frame paints the ring via `:focus-within`;
 * the inner buttons suppress the browser default outline so the ring isn't
 * doubled up.
 */
const FRAME_FOCUS_RING = 'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring';
const INNER_BUTTON_FOCUS_RESET = 'outline-none focus-visible:outline-none';

const FRAME_VARIANT_CLASSES: Record<
  FilterChipVariant,
  { base: string; inactive: string; active: string }
> = {
  default: {
    base: 'border bg-card',
    inactive: 'border-border',
    active: 'bg-accent!',
  },
  ghost: {
    base: 'border-0 bg-transparent',
    inactive: '',
    active: '',
  },
};

interface FilterChipTriggerButtonProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
  /** React 19 ref-as-prop: forwarded to the underlying `<button>`. */
  ref?: Ref<HTMLButtonElement>;
}

function FilterChipTriggerButton({
  children,
  className,
  ref,
  ...props
}: FilterChipTriggerButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      className={[
        // `h-full` rather than a fixed height: the frame owns the size rung and
        // has a border, so a child pinned to the frame's own height overflows its
        // content box and gets clipped by `overflow-hidden` -- which reads as a
        // label sitting a pixel low with its descenders shaved off.
        'flex h-full min-w-0 flex-1 cursor-pointer items-center gap-1.5 border-0 bg-transparent pl-3 pr-2 text-sm text-foreground transition-colors hover:bg-accent',
        INNER_BUTTON_FOCUS_RESET,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}

FilterChipTriggerButton.displayName = 'FilterChipTriggerButton';

/**
 * Jira-style filter chip + popover.
 *
 *   Inactive (no filter applied) — `{label} ▾`. The "All" reset row in the
 *     popover communicates the no-filter state; the trigger doesn't repeat
 *     it. Symmetric across single/multi.
 *   Single mode (default) — value: TValue | null. Selecting "All" clears.
 *     Active trigger: `{label}: {valueLabel}` + X clear button.
 *   Multi mode — value: TValue[]. Selecting toggles; "All" clears to []. Stays
 *     open while the user picks. Active trigger: `{label} {count}` (count
 *     rendered as a primary pill) + X clear — matches Jira's "Epic 3" pattern.
 *
 * Same row visuals across both modes: highlighted bg + trailing check icon
 * for selected items.
 *
 * Async mode (opt-in via `asyncOptions`) — the search input is controlled by
 *   the caller (server-side filtering), `options` render as-is, and a bottom
 *   sentinel triggers `onLoadMore` for infinite scroll. A loading row shows
 *   while a page is in flight. Keyboard nav works over the loaded options.
 *   `hideReset` drops the "All"/clear affordances for required single-selects.
 *
 * Keyboard nav:
 *   - Enter / Space on the trigger opens the popover (Radix default).
 *   - On open, focus moves to the search input when `searchable`, otherwise
 *     to the first option button.
 *   - ArrowDown / ArrowUp move focus through the option list (rolls over the
 *     "All" reset row). Home / End jump to first / last.
 *   - From the search input, ArrowDown moves into the option list.
 *   - Enter / Space toggle the focused option.
 *   - Escape closes the popover and returns focus to the trigger (Radix
 *     default).
 *   - When a filter is active, Tab from the trigger reaches the inline X
 *     clear button; Enter / Space clears.
 */
export function FilterChipPopover<TValue extends string>(props: FilterChipPopoverProps<TValue>) {
  const { t } = useTranslation('common');

  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');

  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { asyncOptions } = props;
  const isAsync = asyncOptions !== undefined;

  // In async mode the search input is controlled by the caller (server filters);
  // otherwise we own it and filter `options` client-side.
  const search = isAsync ? asyncOptions.search : internalSearch;
  const setSearch = (next: string) => {
    if (isAsync) {
      asyncOptions.onSearchChange(next);
    } else {
      setInternalSearch(next);
    }
  };

  const resetLabel = props.resetOptionLabel ?? t('ui.filterChipPopover.all');
  const showReset = props.hideReset !== true;

  const isSearchable = props.searchable !== false;

  // Async options arrive pre-filtered from the server, so render them as-is.
  const visibleOptions =
    isAsync || !isSearchable
      ? props.options
      : props.options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase().trim()));

  const isMulti = props.mode === 'multiple';
  const isActive = isMulti ? props.value.length > 0 : props.value !== null;

  const closePopover = () => {
    setOpen(false);
    setSearch('');
  };

  const hasNextPage = asyncOptions?.hasNextPage ?? false;
  const onLoadMore = asyncOptions?.onLoadMore;

  // Lazy-load the next page when the bottom sentinel scrolls into view. A
  // callback ref wires the observer the moment the sentinel mounts, which is
  // more reliable than a layout effect: the popover content is portaled, so its
  // node may not be attached when a parent effect runs. The `node === null`
  // branch is React's detach signal (fires when the sentinel unmounts OR when
  // the popover itself unmounts) and disconnects the observer, so a separate
  // mount-effect cleanup is not needed (see no-use-effect skill, callback-ref
  // section).
  const attachSentinel = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          onLoadMore?.();
        }
      });
      // NOTE: cleanup for this subscription is returned from the callback ref
      // below (React 19 idiom). react-doctor's `effect-needs-cleanup` flags this
      // as a false positive; the returned function runs on detach/unmount.
      observer.observe(node);
      observerRef.current = observer;

      return () => {
        observer.disconnect();
        if (observerRef.current === observer) {
          observerRef.current = null;
        }
      };
    },
    [hasNextPage, onLoadMore],
  );

  const handleClear = () => {
    if (isMulti) {
      props.onChange([]);
    } else {
      props.onChange(null);
    }
    closePopover();
  };

  const toggleOption = (value: TValue) => {
    if (isMulti) {
      const isSelected = props.value.includes(value);
      const next = isSelected ? props.value.filter((v) => v !== value) : [...props.value, value];
      props.onChange(next);
      // Stay open in multi-mode so users can pick more than one in a row.
    } else {
      props.onChange(value);
      closePopover();
    }
  };

  const isOptionSelected = (value: TValue) =>
    isMulti ? props.value.includes(value) : props.value === value;

  const allRowSelected = isMulti ? props.value.length === 0 : props.value === null;

  const focusOption = (index: number) => {
    const buttons = optionRefs.current.filter((b): b is HTMLButtonElement => b !== null);
    if (buttons.length === 0) return;
    const clamped = Math.max(0, Math.min(index, buttons.length - 1));
    buttons[clamped]?.focus();
  };

  /**
   * Roving focus inside the option list. ArrowUp from the first option falls
   * back to the search input (when searchable) so users can edit the query
   * without grabbing the mouse.
   */
  const handleListKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const buttons = optionRefs.current.filter((b): b is HTMLButtonElement => b !== null);
    if (buttons.length === 0) return;

    const activeIndex = buttons.findIndex((b) => b === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusOption(activeIndex < 0 ? 0 : activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (activeIndex <= 0) {
          if (isSearchable && searchInputRef.current) {
            searchInputRef.current.focus();
          } else {
            focusOption(buttons.length - 1);
          }
        } else {
          focusOption(activeIndex - 1);
        }
        break;
      case 'Home':
        event.preventDefault();
        focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        focusOption(buttons.length - 1);
        break;
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(0);
    }
  };

  const variant: FilterChipVariant = props.variant ?? 'default';
  const variantClasses = FRAME_VARIANT_CLASSES[variant];
  const frameBaseClasses = `inline-flex ${CHIP_FRAME_HEIGHT_CLASS[props.size ?? 'md']} ${CHIP_MAX_WIDTH_CLASS} items-stretch overflow-hidden rounded-md transition-[box-shadow,border-color] ${variantClasses.base} ${FRAME_FOCUS_RING}`;
  const frameStateClasses = isActive ? variantClasses.active : variantClasses.inactive;

  const ChevronIcon = open ? ChevronUpIcon : ChevronDownIcon;
  const chevronEl = <ChevronIcon className="size-3.5 shrink-0 text-muted-foreground" />;

  const triggerLabelStyle = props.triggerLabelStyle ?? 'label';

  // single call site, large closure (label/value/options across single-
  // and multi-select modes) — inlined as a computed value rather than a
  // `renderTriggerInner`-style closure with nothing left to thread as props.
  //
  // Weight tracks meaning across all four shapes: the chip's own name leads
  // while nothing is picked, and steps back to a muted prefix once a value
  // takes over. Never both at the same weight.
  let triggerInner: ReactNode;
  if (triggerLabelStyle === 'value') {
    let valueLabel: string;
    if (isMulti) {
      valueLabel = props.value.length === 0 ? resetLabel : `${resetLabel} (${props.value.length})`;
    } else {
      valueLabel = props.value
        ? (props.options.find((o) => o.value === props.value)?.label ?? resetLabel)
        : resetLabel;
    }
    triggerInner = (
      <>
        <span className="min-w-0 truncate font-medium">{valueLabel}</span>
        {chevronEl}
      </>
    );
  } else if (!isActive) {
    triggerInner = (
      <>
        <span className="shrink-0 font-medium">{props.label}</span>
        {chevronEl}
      </>
    );
  } else if (isMulti) {
    triggerInner = (
      <>
        <span className="shrink-0 text-muted-foreground">{props.label}</span>
        <CountBadge count={props.value.length} />
      </>
    );
  } else {
    const selectedLabel = props.options.find((o) => o.value === props.value)?.label ?? resetLabel;
    triggerInner = (
      <>
        <span className="shrink-0 text-muted-foreground">{props.label}:</span>
        <span className="min-w-0 truncate font-medium">{selectedLabel}</span>
      </>
    );
  }

  // When the chip label is dropped from the trigger, the inline X-clear becomes
  // visually disconnected from any "active" cue, so we hide it. Users can clear
  // via the "All" reset row inside the popover. `hideReset` also suppresses it:
  // a required single-select has no cleared state to fall back to.
  const showInlineClear = isActive && triggerLabelStyle !== 'value' && showReset;

  // Reset the option-ref array on each render so stale slots from a previous
  // filtered list don't leak into roving focus calculations.
  optionRefs.current = [];
  let optionRefIndex = 0;
  const registerOptionRef = (el: HTMLButtonElement | null) => {
    optionRefs.current[optionRefIndex++] = el;
  };

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : closePopover())}>
      <div
        className={[frameBaseClasses, frameStateClasses, props.frameClassName]
          .filter(Boolean)
          .join(' ')}
      >
        <PopoverTrigger asChild>
          <FilterChipTriggerButton
            /* The multi variant shows its count in a CountBadge inside this
               button, but an aria-label short-circuits name-from-content, so
               the count has to be spelled out here too. */
            aria-label={
              props.triggerAriaLabel ??
              (isMulti && isActive
                ? t('ui.filterChipPopover.filterByWithCount', {
                    label: props.label.toLowerCase(),
                    count: props.value.length,
                  })
                : t('ui.filterChipPopover.filterBy', { label: props.label.toLowerCase() }))
            }
            className={props.triggerClassName}
          >
            {props.leadingIcon}
            {triggerInner}
          </FilterChipTriggerButton>
        </PopoverTrigger>
        {showInlineClear && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('ui.filterChipPopover.clearFilter', { label: props.label.toLowerCase() })}
            className={`flex shrink-0 cursor-pointer items-center justify-center border-0 border-l border-border bg-transparent px-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground ${INNER_BUTTON_FOCUS_RESET}`}
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent
        side={props.side ?? 'bottom'}
        align="start"
        className="flex w-65 flex-col overflow-hidden p-0"
      >
        {props.popoverHeader && (
          <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
            {props.popoverHeader}
          </div>
        )}
        {isSearchable && (
          <div className="px-3 py-2">
            <InputGroup
              size="sm"
              className="w-full has-[input:focus-visible]:ring-0 has-[input:focus-visible]:ring-offset-0"
            >
              <InputDecoration muted>
                <SearchIcon className="size-3.5" />
              </InputDecoration>
              <Input
                ref={searchInputRef}
                placeholder={
                  props.searchPlaceholder ??
                  t('ui.filterChipPopover.search', { label: props.label.toLowerCase() })
                }
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                aria-label={t('ui.filterChipPopover.searchOptions', {
                  label: props.label.toLowerCase(),
                })}
              />
            </InputGroup>
          </div>
        )}

        <div
          className="min-h-0 max-h-75 flex-1 overflow-y-auto py-1"
          // When the popover renders inside a Radix Dialog (e.g. the Add
          // Artifacts modal), `react-remove-scroll` listens for wheel
          // events at the document level and cancels them outside the
          // dialog. The portaled popover content sits outside the dialog
          // tree, so its native wheel scroll is blocked. Handling the wheel
          // event here and applying scrollBy ourselves bypasses the lock.
          onWheel={(event) => {
            event.currentTarget.scrollBy({ top: event.deltaY });
          }}
        >
          {showReset && (
            <button
              ref={registerOptionRef}
              type="button"
              onClick={handleClear}
              onKeyDown={handleListKeyDown}
              className={[
                'flex w-full cursor-pointer items-center gap-2 border-0 px-3 py-1.5 text-left text-sm transition-colors',
                allRowSelected
                  ? 'bg-accent text-primary'
                  : 'bg-transparent text-foreground hover:bg-accent',
                'focus-visible:bg-accent focus-visible:outline-none',
              ].join(' ')}
            >
              <span className="flex-1 truncate">{resetLabel}</span>
              {allRowSelected && <CheckIcon className="size-4 shrink-0 text-primary" />}
            </button>
          )}

          {visibleOptions.length === 0 && !asyncOptions?.isLoading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('ui.filterChipPopover.noOptions')}
            </div>
          ) : (
            visibleOptions.map((option) => {
              const selected = isOptionSelected(option.value);
              return (
                <button
                  ref={registerOptionRef}
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  onKeyDown={handleListKeyDown}
                  className={[
                    'flex w-full cursor-pointer items-center gap-2 border-0 px-3 py-1.5 text-left text-sm transition-colors',
                    selected
                      ? 'bg-accent text-primary'
                      : 'bg-transparent text-foreground hover:bg-accent',
                    'focus-visible:bg-accent focus-visible:outline-none',
                  ].join(' ')}
                >
                  {option.icon && <span className="flex shrink-0 items-center">{option.icon}</span>}
                  <span className="flex-1 truncate">{option.label}</span>
                  {selected && <CheckIcon className="size-4 shrink-0 text-primary" />}
                </button>
              );
            })
          )}

          {isAsync && asyncOptions.isLoading && (
            <div className="flex items-center justify-center py-3">
              <Spinner />
            </div>
          )}
          {isAsync && <div ref={attachSentinel} aria-hidden className="h-px w-full shrink-0" />}
        </div>
      </PopoverContent>
    </Popover>
  );
}
